import { AfterViewInit, Component, DoCheck, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { KeyHandlerService, SecurityService, BrowserService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { PropertyMetadata, RegexFormattingOptions } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { AugmentedModelObject, ValueChangedParms } from "../../../../interfaces";
import { MetadataInputFormatterService, MetadataInputTypeHelperService, MetadataPropertyService, MetadataService } from "../../../../services";

@Component({
    selector: "metadata-input-text",
    templateUrl: "./metadata-input-text.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataInputTextComponent implements OnInit, AfterViewInit, OnChanges, DoCheck, OnDestroy
{
    @Input()
    model: string;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    viewOnly: boolean;

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    dense: boolean;

    @Input()
    forCellEdit: boolean;

    @Input()
    hint: string;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    error: string;

    @Input()
    compact: boolean;

    @Input()
    showSpinner: boolean;

    @ViewChild("input") input: ElementRef;

    /** Accessible in the template */ isViewOnly: boolean;

    private isInitialized: boolean;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: string;

    private _valueModel: string = "";

    private currentLength: number = 0;

    private readonly debouncer$ = new Subject<void>();
    private readonly destroy$ = new Subject<void>();

    private _density: string;

    constructor(
        public modelGroup: NgModelGroup,
        private $metadataService: MetadataService,
        private readonly $metadataPropertyService: MetadataPropertyService,
        private readonly $metadataInputFormatterService: MetadataInputFormatterService,
        private readonly $metadataInputTypeHelperService: MetadataInputTypeHelperService,
        private readonly $keyHandlerService: KeyHandlerService,
        private readonly $securityService: SecurityService,
    ) { }

    ngOnInit(): void
    {
        this.model = this.applyFormatting(this.model);
        this.unboundModel = this.model;
        this.currentLength = this.model ? this.model.length : 0;

        if (this.forCellEdit)
            this.debouncer$.pipe(debounceTime(10), takeUntil(this.destroy$)).subscribe(() => this.fireValueChanged());

        this._density = this.$metadataService.getInputDensity(this.compact);
    }

    ngOnDestroy(): void
    {
        this.input = null;
        if (this.forCellEdit)
        {
            this.destroy$.next();
            this.destroy$.complete();
            this.debouncer$.complete();
        }
    }

    ngAfterViewInit(): void
    {
        if (this.isInitialized)
            return;
        this.isInitialized = true;
    }

    @HostListener("focusin", ["$event"])
    onFocus(event: any)
    {
        if (!this.forCellEdit)
            this.input.nativeElement.select();
    }

    ngDoCheck(): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        if (this.unboundModel !== this.model)
        {
            this.onModelChanged(this.model);
        }
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent);
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    const change = _changes[key];
                    this.onModelChanged(change.currentValue);
                    break;
            }
        }
    }

    get isInvalid(): boolean
    {
        return this.$metadataService.invalidInput(this.error, this.modelGroup?.control?.controls[this.propertyMetadata.Name]);
    }

    private onModelChanged(value: string): void
    {
        this.unboundModel = value;
        if (!value)
        {
            this.valueModel = null;
            this.currentLength = 0;
        }
        else if (!this.valueModel || this.valueModel !== value)
        {
            value = this.applyFormatting(value);
            this.valueModel = value;
            this.currentLength = this.valueModel ? this.valueModel.length : 0;
        }
    }

    get valueModel(): string
    {
        return this._valueModel;
    }

    set valueModel(value: string)
    {
        if (Utilities.IsNullOrUndefined(value) && this.model === "" && this.isRequired) this._valueModel = "";
        else this._valueModel = value;
    }

    get InputType(): string
    {
        if (Utilities.IsNullOrUndefined(this.propertyMetadata)) return "";
        return this.propertyMetadata.InputType;
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    get Label(): string
    {
        return this.forCellEdit
            ? ""
            : this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    get Density(): string
    {
        return this._density;
    }

    get LabelPosition(): string
    {
        return this.forCellEdit ? 'none' : this.compact ? 'block-start' : 'inset';
    }

    get Regex(): string
    {
        return this.$metadataPropertyService.getRegex(this.propertyMetadata, this.metadataModel, this.parent);
    }

    get RegexOptions(): RegexFormattingOptions
    {
        return this.$metadataPropertyService.getRegexFormatter(this.propertyMetadata, this.metadataModel, this.parent);
    }

    get MinLength(): number
    {
        return this.propertyMetadata.MinLength;
    }

    get MaxLength(): number
    {
        return this.$metadataInputTypeHelperService.getMaxLength(this.propertyMetadata, this.metadataModel, this.parent);
    }

    GetToolTipText(): string
    {
        return this.propertyMetadata.TooltipText;
    }

    HasTooltip(): boolean
    {
        return !!this.propertyMetadata && !!this.propertyMetadata.Tooltip;
    }

    Rows(): number
    {
        if (this.propertyMetadata.Rows) return this.propertyMetadata.Rows;
        return 5;
    }

    get isRequired(): boolean
    {
        return (
            this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent) &&
            !this.isViewOnly
        );
    }

    onInputChange(): void
    {
        if (this.forCellEdit)
            this.debouncer$.next();
    }

    onChange(): void
    {
        if (!this.forCellEdit)
            this.fireValueChanged();
    }

    fireValueChanged(): void
    {
        if (this.RegexOptions && this.RegexOptions.BackEndValueRegexReplace)
        {
            const currentModel = this.$metadataInputFormatterService.GetFormattedModel(this.valueModel, this.RegexOptions);
            if (currentModel === this.model)
            {
                return;
            }
            this.onValueChanged.emit({ value: currentModel });
            return;
        }
        if (this.model === this.valueModel)
        {
            return;
        }

        this.valueModel = this.applyFormatting(this.valueModel);
        this.onValueChanged.emit({ value: this.valueModel });

    }

    applyFormatting(model: any): any
    {
        if (this.RegexOptions)
            model = this.$metadataInputFormatterService.GetFormattedValueModel(
                model,
                this.RegexOptions
            );
        return model;
    }

    enforceFormat(event: KeyboardEvent, textArea = false): void
    {
        if(event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
        else if (!textArea && this.RegexOptions && this.RegexOptions.ValidCharRegex && !this.$keyHandlerService.ignoreForFormatting(event))
        {
            const keyHit = event.key;
            if (keyHit.match(new RegExp(this.RegexOptions.ValidCharRegex)) == null)
            {
                event.preventDefault();
            }
        }
    }

    updateCurrentLength(event): void
    {
        this.currentLength = event.target.value.length;
        if (this.RegexOptions)
        {
            if (!this.$keyHandlerService.ignoreForFormatting(event))
            {
                event.target.value = this.$metadataInputFormatterService.GetFormattedValueModel(event.target.value, this.RegexOptions);
                this.valueModel = event.target.value;
            }
        }
    }
}
